import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, FormControl, InputLabel,
  Select, MenuItem
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { db } from "../firebase";
import { salvaLog } from "../utils/logger";
import { useAuth } from "../context/AuthContext";
import {
  collection, onSnapshot, addDoc, deleteDoc,
  doc, query, where, serverTimestamp
} from "firebase/firestore";

const CATEGORIE_COSTO = [
  "Noleggio o acquisto attrezzatura",
  "Trasporto",
  "Parcheggio",
  "Pedaggi",
  "Carburante",
  "Costo kilometrico",
  "Ingaggio professionisti",
  "Servizi per conto del cliente finale",
];

const FORM_VUOTO = {
  categoria: "",
  importo: "",
  data: "",
  fornitore: "",
  collaboratoreId: "",
  note: "",
};

export default function CostiEvento({ eventoId }) {
  const { currentUser } = useAuth(); // aggiungi questa riga
  const [costi, setCosti] = useState([]);
  const [personale, setPersonale] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(FORM_VUOTO);
  const [saving, setSaving] = useState(false);

  // Carica costi per questo evento
  useEffect(() => {
    if (!eventoId) return;
    const q = query(
      collection(db, "costi"),
      where("eventoId", "==", eventoId)
    );
    const unsub = onSnapshot(q, (snap) => {
      setCosti(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [eventoId]);

  // Carica personale ingaggiato per questo evento
  useEffect(() => {
    if (!eventoId) return;
    const q = query(
      collection(db, "personaleEvento"),
      where("eventoId", "==", eventoId)
    );
    const unsub = onSnapshot(q, (snap) => {
      setPersonale(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [eventoId]);

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSalva() {
    if (!form.categoria || !form.importo) return;
    setSaving(true);
    try {
      const collaboratoreSelezionato = personale.find((p) => p.id === form.collaboratoreId);
      await addDoc(collection(db, "costi"), {
        ...form,
        eventoId,
        nominativoSpesa: collaboratoreSelezionato?.nominativo || "Cassa aziendale",
        createdAt: serverTimestamp(),
      });
      salvaLog(currentUser, "Aggiunta costo", eventoId, "", [
        { campo: "Categoria", da: "—", a: form.categoria },
        { campo: "Importo", da: "—", a: `€ ${form.importo}` },
      ]);
      setForm(FORM_VUOTO);
      setDialogOpen(false);
    } catch (error) {
      console.error("Errore salvataggio costo:", error);
    }
    setSaving(false);
  }

  async function handleElimina(id) {
    const c = costi.find((c) => c.id === id);
    try {
      await deleteDoc(doc(db, "costi", id));
      salvaLog(currentUser, "Rimozione costo", eventoId, "", [
        { campo: "Categoria", da: c?.categoria || "—", a: "—" },
        { campo: "Importo", da: `€ ${c?.importo || 0}`, a: "—" },
      ]);
    } catch (error) {
      console.error("Errore eliminazione costo:", error);
    }
  }

  const totale = costi.reduce((acc, c) => acc + (parseFloat(c.importo) || 0), 0);

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold" color="primary">
          Costi evento
        </Typography>
        <Button variant="outlined" size="small" startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}>
          Aggiungi costo
        </Button>
      </Box>

      {costi.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nessun costo registrato per questo evento.
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                  <TableCell><strong>Categoria</strong></TableCell>
                  <TableCell><strong>Importo</strong></TableCell>
                  <TableCell><strong>Data</strong></TableCell>
                  <TableCell><strong>Fornitore</strong></TableCell>
                  <TableCell><strong>A carico di</strong></TableCell>
                  <TableCell><strong>Note</strong></TableCell>
                  <TableCell align="center"><strong>Elimina</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {costi.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.categoria}</TableCell>
                    <TableCell>€ {parseFloat(c.importo).toFixed(2)}</TableCell>
                    <TableCell>{c.data || "—"}</TableCell>
                    <TableCell>{c.fornitore || "—"}</TableCell>
                    <TableCell>{c.nominativoSpesa || "—"}</TableCell>
                    <TableCell>{c.note || "—"}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error"
                        onClick={() => handleElimina(c.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={1} display="flex" justifyContent="flex-end">
            <Typography variant="body2" fontWeight="bold">
              Totale: € {totale.toFixed(2)}
            </Typography>
          </Box>
        </>
      )}

      {/* DIALOG NUOVO COSTO */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aggiungi voce di costo</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Categoria</InputLabel>
                <Select value={form.categoria} label="Categoria"
                  onChange={(e) => handleChange("categoria", e.target.value)}>
                  {CATEGORIE_COSTO.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth required label="Importo (€)" type="number"
                value={form.importo}
                onChange={(e) => handleChange("importo", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Data" type="date" value={form.data}
                onChange={(e) => handleChange("data", e.target.value)}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fornitore" value={form.fornitore}
                onChange={(e) => handleChange("fornitore", e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Chi ha sostenuto la spesa</InputLabel>
                <Select value={form.collaboratoreId}
                  label="Chi ha sostenuto la spesa"
                  onChange={(e) => handleChange("collaboratoreId", e.target.value)}>
                  <MenuItem value="">Cassa aziendale</MenuItem>
                  {personale.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.nominativo}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Note" value={form.note}
                onChange={(e) => handleChange("note", e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSalva}
            disabled={saving || !form.categoria || !form.importo}>
            {saving ? <CircularProgress size={20} /> : "Salva"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}